/**
 * @fileoverview Hook for fetching a question from the API
 *
 * @example
 * // Usage:
 * import { useQuestion } from './useQuestion'
 *
 * function Component() {
 *   const question = useQuestion()
 *   // ...
 * }
 */

import axios, { AxiosError, AxiosResponse } from 'axios'
import { useEffect, useState } from 'react'

/**
 * @function fetchData
 * @description Fetches a question from the API
 * @returns {[Novoic.AnyQuestion, number]} An array containing the question and the status
 */
async function fetchData(): Promise<[Novoic.AnyQuestion, number]> {
    const { data, status } = await axios.get<Novoic.AnyQuestion, AxiosResponse<Novoic.AnyQuestion>>(
        `${process.env.API_HOST}`
    )
    return [data, status]
}

/**
 * @function useQuestion
 * @description Hook for fetching a question from the API
 * @param {string} assignmentId - The ID of the assignment
 * @returns {[Novoic.AnyQuestion, number, AxiosError]} An array containing the question, status, and error
 */
export function useQuestion(assignmentId: string): [Novoic.AnyQuestion, number, AxiosError] {
    const [question, setQuestion] = useState<Novoic.AnyQuestion>()
    const [status, setStatus] = useState<number>()
    const [error, setError] = useState<AxiosError>()

    useEffect(() => {
        async function getQuestion() {
            try {
                const [question, status] = await fetchData()
                setStatus(status)
                setQuestion(question)
            } catch (e) {
                const error = e as AxiosError
                setStatus(error.response.status)
                setError(e)
            }
        }

        getQuestion()
    }, [assignmentId])

    return [question, status, error]
}
